import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Menu, ArrowUpRight } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ASSETS } from "@/data/content";

const LINKS = [
  { label: "The Vision", href: "#vision" },
  { label: "The Edition", href: "#edition" },
  { label: "Voices", href: "#voices" },
  { label: "The Almanac", href: "#almanac" },
];

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      data-testid="site-nav"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-500 ${
        scrolled ? "bg-cream/90 backdrop-blur-md border-b border-clay/70" : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-[1400px] items-center justify-between px-5 py-4 md:px-10">
        <a href="#top" data-testid="nav-logo" className="flex items-center gap-3">
          <img src={ASSETS.carbon8Mark} alt="Carbon8" className="h-7 w-auto" />
          <span className="leading-none">
            <span className="block font-display text-2xl font-semibold tracking-tight text-forest">NRAD</span>
            <span className="block font-body text-[10px] uppercase tracking-[0.32em] text-ink/60">The Almanac</span>
          </span>
        </a>

        <nav className="hidden items-center gap-9 lg:flex">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.href.slice(1)}`}
              className="group relative font-body text-sm text-ink/75 transition-colors hover:text-ink"
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-ochre transition-all duration-300 group-hover:w-full" />
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <a
            href="#reserve"
            data-testid="nav-reserve-btn"
            className="hidden items-center gap-1.5 rounded-full bg-forest px-5 py-2.5 font-body text-sm font-medium text-cream transition-transform duration-300 hover:-translate-y-0.5 sm:inline-flex"
          >
            Reserve Your Page <ArrowUpRight size={15} />
          </a>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="nav-mobile-trigger"
                className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-clay text-ink lg:hidden"
                aria-label="Open menu"
              >
                <Menu size={18} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52 border-clay bg-paper">
              {LINKS.map((l) => (
                <DropdownMenuItem key={l.href} asChild>
                  <a href={l.href} data-testid={`nav-mobile-${l.href.slice(1)}`} className="font-body">
                    {l.label}
                  </a>
                </DropdownMenuItem>
              ))}
              <DropdownMenuItem asChild>
                <a href="#reserve" className="font-body font-medium text-forest">Reserve Your Page</a>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </motion.header>
  );
};
